import React, { useState, useMemo, useEffect } from 'react';
import { Privilege } from '../../api';
import pitchesService, { PitchDetails, PitchInput } from '../../services/pitches';
import { refreshPitches } from './usePitches';
import { useSnackbar } from 'notistack';
import styled from 'styled-components';
import MarkerMap from '../Util/MarkerMap';
import { Button, Input } from '@material-ui/core';
import { RouteComponentProps, withRouter } from 'react-router';
import DeletePitch from '../Util/DeletePitch';
import useHasPrivilege from '../Util/useHasPrivilege';
import SelectSinglePitchType from '../Util/SelectSinglePitchType';

interface Props extends RouteComponentProps {
  pitch: PitchDetails;
}

const PitchProperties: React.FunctionComponent<Props> = (props) => {
  const { enqueueSnackbar } = useSnackbar();
  const [edit, setEdit] = useState(false);
  const [pitchType, setPitchType] = useState(props.pitch.type || undefined);
  const [name, setName] = useState(props.pitch.name);
  const [fixedSize, setFixedSize] = useState(props.pitch.type === 'HockeyPitchModel');
  const [fieldWidth, setFieldWidth] = useState(props.pitch.fieldWidth);
  const [fieldLength, setFieldLength] = useState(props.pitch.fieldLength);
  const [lat, setLat] = useState<number>(props.pitch.latitude);
  const [lng, setLng] = useState<number>(props.pitch.longitude);
  const canWritePitches = useHasPrivilege(Privilege.WritePitches);

  useMemo(() => {
    setPitchType(props.pitch.type || undefined);
    setName(props.pitch.name);
    setFieldLength(props.pitch.fieldLength);
    setFieldWidth(props.pitch.fieldWidth);
    setLat(props.pitch.latitude);
    setLng(props.pitch.longitude);
    setEdit(props.pitch.id === -1);
  }, [props.pitch]);

  useEffect(() => {
    if (pitchType === 'HockeyPitchModel') {
      setFixedSize(true);
      setFieldWidth(55000);
      setFieldLength(91400);
    } else {
      setFixedSize(false);
    }
  }, [pitchType]);

  const onEditSave = () => {
    if (edit) {
      onSave();
    } else {
      setEdit(true);
    }
  };

  const onCancel = () => {
    setEdit(false);
  };

  const setLatlng = (lat: number, lng: number) => {
    setLat(lat);
    setLng(lng);
  };

  const onSave = () => {
    let valid = true;
    if (name.toLowerCase().trim() === '') {
      enqueueSnackbar('You must enter a name for the pitch', { variant: 'info', autoHideDuration: 3000 });
      valid = false;
    }
    if (!lat || !lng) {
      enqueueSnackbar('You must pick the pitch location on the map ', {
        variant: 'info',
        autoHideDuration: 3000,
      });
      valid = false;
    }
    if (valid && lat && lng) {
      const input: PitchInput = {
        name: name,
        type: pitchType || 'FullPitchExtendedModel',
        fieldLength: fieldLength,
        fieldWidth: fieldWidth,
        latitude: lat,
        longitude: lng,
        metadata: props.pitch.metadata,
      };
      if (props.pitch.id !== -1) {
        pitchesService.updatePitch(props.pitch.id, input).then(
          () => {
            setEdit(false);
            refreshPitches();
          },
          () => enqueueSnackbar('Failed to update the pitch', { variant: 'error', autoHideDuration: 3000 })
        );
      } else {
        pitchesService.addPitch(input).then(
          (pitch) => {
            setEdit(false);
            refreshPitches().then(() => props.history.push('/pitch/' + pitch.id));
          },
          () => enqueueSnackbar('Failed to add the pitch', { variant: 'error', autoHideDuration: 3000 })
        );
      }
    }
  };

  return (
    <div>
      <h2>The pitch</h2>
      <Container>
        <FieldContainer>
          <Field>
            <Key>Name</Key>
            <Value>
              <ValueField value={name} readOnly={!edit} onChange={setName} />
            </Value>
          </Field>
          <Field>
            <Key>Pitch layout</Key>
            <Value>
              <SelectSinglePitchType value={pitchType} onChange={setPitchType} readOnly={!edit} />
            </Value>
          </Field>
          <Field>
            <Key>Field width (mm)</Key>
            <Value>
              <ValueField value={fieldWidth} readOnly={!edit || fixedSize} onChange={setFieldWidth} />
            </Value>
          </Field>
          <Field>
            <Key>Field length (mm)</Key>
            <Value>
              <ValueField value={fieldLength} readOnly={!edit || fixedSize} onChange={setFieldLength} />
            </Value>
          </Field>
          <Field>
            <Key>Timezone</Key>
            <Value>{props.pitch.timeZone}</Value>
          </Field>
        </FieldContainer>
        {canWritePitches && (
          <ButtonContainer>
            <Button onClick={onEditSave} variant="outlined" color="primary">
              {edit ? 'Save' : 'Edit'}
            </Button>
            {edit && (
              <Button onClick={onCancel} variant="outlined" color="primary">
                Cancel
              </Button>
            )}
            {edit && (
              <DeletePitch
                enabled={true}
                pitchName={props.pitch.name}
                id={String(props.pitch.id)}
                onDeleted={() => refreshPitches().then(() => props.history.push('/pitch'))}
              />
            )}
          </ButtonContainer>
        )}
      </Container>
      <MarkerMap width="100%" height="300px" lat={lat} lng={lng} onChange={setLatlng} readOnly={!edit} />
    </div>
  );
};

type ValueFieldProps<T> = {
  readOnly: boolean;
  value: T;
  onChange: (value: T) => void;
};

function ValueField<T>(props: ValueFieldProps<T>) {
  if (props.readOnly) {
    return <span>{props.value}</span>;
  } else {
    return (
      <Input
        id="name"
        defaultValue={props.value}
        type={typeof props.value === 'number' ? 'number' : 'text'}
        onChange={(event) =>
          props.onChange(
            typeof props.value === 'number' ? (parseInt(event.target.value) as any) : (event.target.value as any)
          )
        }
        inputProps={{
          readOnly: props.readOnly,
        }}
      />
    );
  }
}

const FieldContainer = styled.div`
  margin-top: 32px;
  margin-bottom: 32px;
  width: 80%;
  display: flex;
  flex-direction: column;
`;

const Field = styled.div`
  display: flex;
  flex-direction: row;
`;

const Key = styled.div`
  flex-basis: 200px;
  flex-grow: 1;
  font-weight: 600;
`;

const Value = styled.div`
  flex-basis: 500px;
  flex-grow: 1;
  font-weight: 300;
`;

const Container = styled.div`
  display: flex;
  flex-direction: row;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 20%;
  margin-top: 32px;
  height: 40px;
  justify-content: flex-end;
`;
export default withRouter(PitchProperties);
